import React from 'react'
import { format } from 'date-fns'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { TickCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'

import OverflowText from '../../../../atoms/OverflowText'

import { selectConvo } from '../../../../../slices/convo'
import { useUserAPI } from '../../../../../providers/UserAPIProvider'
import { useAgent } from '../../../../../providers/AgentProvider'
import { useBreakpoint } from '../../../../../providers/BreakpointProvider'
import useStyles from './styles'

const DividerMessage = ({
  id,
  group,
  names,
  formattedDate,
  isEnd,
  verified
}) => {
  const theme = useTheme()
  const { allConversations } = useAppSelector(selectConvo)

  const [reason, setReason] = React.useState(null)

  const classes = useStyles()

  React.useEffect(() => {
    const convo = allConversations[id]
    if (convo) {
      setReason(convo.topic)
    }
  }, [allConversations, id])

  if (isEnd) {
    return (
      <label className={classes.dividerLabel}>
        {group ? `Group` : `Live`} chat conversation ended
        {'  '}
        {formattedDate}
      </label>
    )
  }

  return (
    <div className={classes.root}>
      <OverflowText className={classes.dividerLabel}>
        {group ? `Group` : `Live`} chat conversation with{' '}
        <strong>
          {names.length > 0 ? `${names.join(', ')} and You` : `You`}
        </strong>{' '}
        {formattedDate}
        {reason && ` | Topic: ${reason}`}
      </OverflowText>
      <span className={classes.verifiedIcon}>
        <TickCircle
          size={theme.size.base}
          variant='Bold'
          color={verified ? '#06C270' : '#FF3B3B'}
        />
      </span>
    </div>
  )
}

const MobileDividerMessage = ({
  id,
  group,
  names,
  formattedDate,
  isEnd,
  verified
}) => {
  const { allConversations } = useAppSelector(selectConvo)

  const [reason, setReason] = React.useState(null)

  const theme = useTheme()
  const classes = useStyles()

  React.useEffect(() => {
    const convo = allConversations[id]
    if (convo) {
      setReason(convo.topic)
    }
  }, [allConversations, id])

  if (isEnd) {
    return (
      <label className={classes.dividerLabel}>
        {group ? `Group` : `Live`} chat conversation ended
        {'  '}
        {formattedDate}
      </label>
    )
  }

  return (
    <div className={classes.root}>
      <div>
        <OverflowText className={classes.mobileDividerLabel}>
          {group ? `Group` : `Live`} chat conversation with{' '}
          <strong>
            {names.length > 0 ? `${names.join(', ')} and You` : `You`}
          </strong>{' '}
        </OverflowText>
        <div className={classes.mobileDividerLabel}>
          {formattedDate}
          {reason && ` | Topic: ${reason}`}
        </div>
      </div>

      <span className={classes.verifiedIcon}>
        <TickCircle
          size={theme.size.base}
          variant='Bold'
          color={verified ? '#06C270' : '#FF3B3B'}
        />
      </span>
    </div>
  )
}

const SessionDividerV2 = ({
  isEnd,
  date,
  group,
  convo,
  verified,
  children
}) => {
  const { allUsers } = useUserAPI()
  const { currentUser } = useAgent()
  const { isSmall } = useBreakpoint()
  const formattedDate = format(new Date(date), 'MMM d, h:mm aaa')
  const classes = useStyles()
  const me = React.useMemo(() => currentUser(), [currentUser])
  const userIds = React.useMemo(
    () =>
      convo?.userIds.filter(u => u !== me?.id && u !== convo?.owner_id) || [],
    [convo, me]
  )
  const names = React.useMemo(() => {
    const list = []
    userIds.forEach(u => {
      list.push(allUsers[u]?.name)
    })
    return list
  }, [userIds, allUsers])

  if (isSmall) {
    return (
      <React.Fragment>
        <div className={classes.start}>
          <MobileDividerMessage
            id={convo?.id}
            group={group}
            names={names}
            formattedDate={formattedDate}
            isEnd={isEnd}
            verified={verified}
          />
        </div>

        {children}
      </React.Fragment>
    )
  }

  return (
    <>
      <div className={classes.start}>
        <DividerMessage
          id={convo?.id}
          group={group}
          names={names}
          formattedDate={formattedDate}
          isEnd={isEnd}
          verified={verified}
        />
      </div>

      {children}
    </>
  )
}

export default SessionDividerV2
